function smoothstep(edge0: number, edge1: number, x: number): number {
  const t = Math.min(1, Math.max(0, (x - edge0) / (edge1 - edge0)));
  return t * t * (3 - 2 * t);
}

export type Cay = {
  cx: number;
  cz: number;
  rx: number;
  rz: number;
  peak: number;
};

/** Home island ellipse. Harbour is cut out of the north coast. */
export const HOME_ISLAND: Cay = {
  cx: 0,
  cz: -118,
  rx: 168,
  rz: 158,
  peak: 22,
};

/** Overlapping ellipses that cut a kidney-shaped inlet instead of a box. */
export const HARBOUR_LOBES: Cay[] = [
  { cx: 6, cz: 4, rx: 44, rz: 36, peak: 0 },
  { cx: -26, cz: 12, rx: 24, rz: 28, peak: 0 },
  { cx: 24, cz: -6, rx: 18, rz: 20, peak: 0 },
  { cx: 8, cz: 42, rx: 15, rz: 18, peak: 0 },
  { cx: 16, cz: 64, rx: 22, rz: 18, peak: 0 },
];

export const DISTANT_CAYS: Cay[] = [
  { cx: -460, cz: 410, rx: 62, rz: 40, peak: 16 },
  { cx: 520, cz: 340, rx: 48, rz: 32, peak: 11 },
  { cx: 90, cz: 680, rx: 78, rz: 52, peak: 24 },
  { cx: -210, cz: 560, rx: 30, rz: 22, peak: 8 },
];

export function sdEllipse(x: number, z: number, c: Cay): number {
  const qx = (x - c.cx) / c.rx;
  const qz = (z - c.cz) / c.rz;
  return (Math.hypot(qx, qz) - 1) * Math.min(c.rx, c.rz);
}

/** Negative on land, positive at sea. Harbour basin is sea. */
export function landSdf(x: number, z: number): number {
  let d = sdEllipse(x, z, HOME_ISLAND);
  const cut = harbourWaterSdf(x, z);
  if (cut < 0) d = Math.max(d, -cut);
  for (const cay of DISTANT_CAYS) d = Math.min(d, sdEllipse(x, z, cay));
  return d;
}

export function harbourWaterSdf(x: number, z: number): number {
  let d = Infinity;
  for (const lobe of HARBOUR_LOBES) d = Math.min(d, sdEllipse(x, z, lobe));
  return d;
}

export function seaDistance(x: number, z: number): number {
  return Math.max(landSdf(x, z), 0);
}

/** 0 on land / in the basin, 1 in open ocean. */
export function waveScale(x: number, z: number): number {
  const sea = seaDistance(x, z);
  if (sea <= 0) return 0;
  const shore = smoothstep(2, 22, sea);
  const open = smoothstep(22, 110, sea);
  const inward = Math.max(-harbourWaterSdf(x, z), 0);
  const harbourCalm = 1 - 0.88 * smoothstep(0, 10, inward);
  return shore * (0.28 + 0.72 * open) * harbourCalm;
}

export function oceanDepth(x: number, z: number): number {
  const sea = seaDistance(x, z);
  const inward = Math.max(-harbourWaterSdf(x, z), 0);
  const basin = smoothstep(0.3, 9, inward) * 3.6;
  const shelf = smoothstep(1, 28, sea) * 7;
  const deep = smoothstep(40, 220, sea) * 55;
  return Math.max(basin, shelf + deep);
}

export function glslCoast(): string {
  const cays = DISTANT_CAYS.map(
    (c, i) =>
      `const vec2 CAY${i}C = vec2(${c.cx.toFixed(1)}, ${c.cz.toFixed(1)});
const vec2 CAY${i}R = vec2(${c.rx.toFixed(1)}, ${c.rz.toFixed(1)});`,
  ).join("\n");
  const cayMins = DISTANT_CAYS.map(
    (_, i) => `  land = min(land, sdEllipse(p, CAY${i}C, CAY${i}R));`,
  ).join("\n");

  const lobes = HARBOUR_LOBES.map(
    (c) =>
      `  d = min(d, sdEllipse(p, vec2(${c.cx.toFixed(1)}, ${c.cz.toFixed(1)}), vec2(${c.rx.toFixed(1)}, ${c.rz.toFixed(1)})));`,
  ).join("\n");

  return `
float sdEllipse(vec2 p, vec2 c, vec2 r) {
  vec2 q = (p - c) / r;
  return (length(q) - 1.0) * min(r.x, r.y);
}

float harbourWaterSdf(vec2 p) {
  float d = 1.0e6;
${lobes}
  return d;
}

${cays}

float landSdf(vec2 p) {
  float land = sdEllipse(p, vec2(${HOME_ISLAND.cx.toFixed(1)}, ${HOME_ISLAND.cz.toFixed(1)}), vec2(${HOME_ISLAND.rx.toFixed(1)}, ${HOME_ISLAND.rz.toFixed(1)}));
  float cut = harbourWaterSdf(p);
  land = max(land, -cut);
${cayMins}
  return land;
}

float waveScaleAt(vec2 p) {
  float sea = max(landSdf(p), 0.0);
  float shore = smoothstep(2.0, 22.0, sea);
  float open = smoothstep(22.0, 110.0, sea);
  float inward = max(-harbourWaterSdf(p), 0.0);
  float harbourCalm = 1.0 - 0.88 * smoothstep(0.0, 10.0, inward);
  return shore * (0.28 + 0.72 * open) * harbourCalm;
}

float oceanDepthAt(vec2 p) {
  float sea = max(landSdf(p), 0.0);
  float inward = max(-harbourWaterSdf(p), 0.0);
  float basin = smoothstep(0.3, 9.0, inward) * 3.6;
  float shelf = smoothstep(1.0, 28.0, sea) * 7.0;
  float deep = smoothstep(40.0, 220.0, sea) * 55.0;
  return max(basin, shelf + deep);
}

vec2 shoreTravel(vec2 p) {
  float e = 2.4;
  vec2 g = vec2(
    landSdf(p + vec2(e, 0.0)) - landSdf(p - vec2(e, 0.0)),
    landSdf(p + vec2(0.0, e)) - landSdf(p - vec2(0.0, e))
  );
  vec2 swell = vec2(0.12, -1.0);
  if (dot(g, g) < 1e-8) return swell;
  vec2 towardLand = -normalize(g);
  float sea = max(landSdf(p), 0.0);
  float wrap = smoothstep(90.0, 14.0, sea);
  return normalize(mix(swell, towardLand, wrap * 0.82));
}
`;
}
